import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { resetPassword } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await resetPassword(token, pw);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-xl shadow-sm p-8 fade-in-up">
        <div className="flex items-center gap-2.5 mb-8">
          <img src="/yd-logo.png" alt="Yogiji Digi Ltd" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-extrabold text-base text-slate-900 leading-tight" style={{ fontFamily: "Manrope" }}>Yogiji Digi</p>
            <p className="text-[10px] uppercase tracking-widest text-slate-500">Analytics Platform</p>
          </div>
        </div>
        {!token ? (
          <p className="text-sm text-red-600" data-testid="reset-missing-token">This reset link is incomplete. Please use the link from your email or <Link to="/login" className="underline font-medium">request a new one</Link>.</p>
        ) : done ? (
          <div className="space-y-4" data-testid="reset-success">
            <div className="flex items-center gap-2 text-emerald-700"><ShieldCheck className="h-5 w-5" /><p className="font-semibold">Password updated</p></div>
            <p className="text-sm text-slate-600">You can now sign in with your new password.</p>
            <Button asChild className="w-full bg-sky-700 hover:bg-sky-800"><Link to="/login" data-testid="reset-go-login">Go to sign in</Link></Button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 mb-1">Set a new password</h1>
            <p className="text-sm text-slate-500 mb-6">Minimum 8 characters. This link works once and expires after 30 minutes.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-pw">New password</Label>
                <Input id="new-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} placeholder="••••••••" data-testid="reset-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pw">Confirm new password</Label>
                <Input id="confirm-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="••••••••" data-testid="reset-confirm-input" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="reset-error-message">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full bg-sky-700 hover:bg-sky-800" data-testid="reset-submit-button">{loading ? "Please wait..." : "Update password"}</Button>
            </form>
            <p className="text-sm text-slate-500 mt-6"><Link to="/login" className="text-sky-700 font-semibold hover:underline" data-testid="reset-back-login">← Back to sign in</Link></p>
          </>
        )}
      </div>
    </div>
  );
}
